// src/Lexbuild/AgentEditorModal.jsx

import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import AgentForm from "./AgentForm";
import AgentFlowEditor from "./AgentFlowEditor";
import { runAgent } from "./LexBuild";
import "./AgentEditorModal.css";

Modal.setAppElement("#root");

export default function AgentEditorModal({
  isOpen,
  onRequestClose,
  existingAgent,
  onSaveNewAgent,
  onUpdateAgent,
}) {
  const [tab, setTab] = useState(1);
  const [draft, setDraft] = useState({
    name: "",
    type: "",
    description: "",
    public: false,
    workflow: [],
    nodes: [],
    edges: [],
  });
  const [runRes, setRunRes] = useState(null);
  const [runErr, setRunErr] = useState(null);
  const [running, setRunning] = useState(false);

  // Hydrate draft (always include nodes/edges/workflow arrays)
  useEffect(() => {
    if (existingAgent) {
      setDraft({
        ...existingAgent,
        nodes: existingAgent.nodes || [],
        edges: existingAgent.edges || [],
        workflow: existingAgent.workflow || [],
      });
    } else {
      setDraft({
        name: "",
        type: "",
        description: "",
        public: false,
        workflow: [],
        nodes: [],
        edges: [],
      });
    }
    setTab(1);
  }, [existingAgent, isOpen]);

  const saveMetadata = (meta) => {
    setDraft((d) => ({ ...d, ...meta }));
    setTab(2);
  };

  // onSave from AgentFlowEditor now gives { workflow, nodes, edges }
  const saveWorkflow = ({ workflow = [], nodes = [], edges = [] }) => {
    const updated = { ...draft, workflow, nodes, edges };
    setDraft(updated);

    if (existingAgent?.id) {
      onUpdateAgent(existingAgent.id, updated);
    } else {
      onSaveNewAgent(updated);
    }

    setTab(1);
  };

  const handleRun = async () => {
    setRunning(true);
    setRunErr(null);
    setRunRes(null);
    try {
      const result = await runAgent(draft, {});
      setRunRes(result);
    } catch (err) {
      setRunErr(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="agent-editor-modal"
      overlayClassName="agent-editor-overlay"
    >
      {/* HEADER */}
      <header className="modal-header">
        <h2>{existingAgent ? "Edit Agent" : "New Agent"}</h2>
        <div className="modal-header-actions">
          <button
            className="lb-secondary-btn"
            onClick={() => setTab(tab === 1 ? 2 : 1)}
          >
            {tab === 1 ? "Switch to Flow" : "Switch to Form"}
          </button>
          {tab === 2 && (
            <>
              <button
                className="lb-secondary-btn"
                onClick={() => setTab(1)}
              >
                Cancel
              </button>
              <button
                className="lb-primary-btn"
                onClick={() => saveWorkflow(draft)}
              >
                Save Workflow
              </button>
            </>
          )}
          <button className="close-btn" onClick={onRequestClose}>
            ✕
          </button>
        </div>
      </header>

      {/* TABS */}
      <div className="modal-tabs">
        <button
          className={tab === 1 ? "active" : ""}
          onClick={() => setTab(1)}
        >
          Configure
        </button>
        <button
          className={tab === 2 ? "active" : ""}
          onClick={() => setTab(2)}
        >
          Workflow
        </button>
        {tab === 1 && draft.workflow.length > 0 && (
          <button
            className="run-btn"
            onClick={handleRun}
            disabled={running}
          >
            {running ? "Running…" : "Run"}
          </button>
        )}
      </div>

      {/* BODY */}
      <div className="modal-body">
        {tab === 1 ? (
          <AgentForm
            initialData={draft}
            onSave={saveMetadata}
            onCancel={onRequestClose}
          />
        ) : (
          <AgentFlowEditor
            initialNodes={draft.nodes}
            initialEdges={draft.edges}
            initialWorkflow={draft.workflow}
            onSave={saveWorkflow}
            onCancel={() => setTab(1)}
          />
        )}
      </div>

      {/* RUN ERRORS / RESULTS */}
      {tab === 1 && runErr && (
        <div className="run-error">Error: {runErr}</div>
      )}
      {tab === 1 && runRes && (
        <pre className="run-result">{JSON.stringify(runRes, null, 2)}</pre>
      )}
    </Modal>
  );
}
